import React, { useState, useRef, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useI18n } from 'contexts/i18n'
import { useLocation, useNavigate } from 'react-router-dom'
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import { omit } from 'utilities/object'
import { cls } from 'utilities/dom'
import isEqual from 'react-fast-compare'
import { ResettableFilterGroup, Filter, Emoji } from './s'
import { X as Reset } from 'styled-icons/feather'
import EntityPicker from 'modals/entity-picker'

const PickCountryField = ({
    entity, picker, optionMapper = (entity => entity),
    name, label, field = null,
    filter, setFilter, allResetAt, salt
}) => {
    const { formatMessage } = useIntl()

    const getDefaultValue = () => field?.defaultValue ?? null
    const getValue = () => filter[name] ?? getDefaultValue()

    const location = useLocation()
    const navigate = useNavigate()

    const [previousAllResetAt, setPreviousAllResetAt] = useState(allResetAt)
    const [picking, setPicking] = useState(false)
    const [isResettable, setIsResettable] = useState(!isEqual(filter[name], getDefaultValue()) && !!filter[name])

    useEffect(() => {
        if(allResetAt !== previousAllResetAt) {
            if(allResetAt.endsWith?.(':update')) {
                setIsResettable(getValue() !== getDefaultValue())
            } else {
                reset({ silent: true })
            }

            setPreviousAllResetAt(allResetAt)
        }
    }, [allResetAt, previousAllResetAt])

    const control = useRef()

    const update = update => {
        if(name in update) {
            setFilter(update)
            setIsResettable(update[name] !== getDefaultValue())
        }
    }

    const reset = (options = {}) => {
        if(name in (location.state?.filter ?? {})) {
            navigate({
                ...location,
                state: {
                    ...location.state,
                    filter: omit(location.state.filter, name)
                },
                replace: true
            })
        }

        const { silent = false } = options
        !silent && setFilter({ [name]: getDefaultValue() })

        setIsResettable(false)
    }

    const filterClassName = cls([
        'constructive',
        picking && 'active',
        isResettable && 'resettable'
    ])

    return (
        <>
            <ResettableFilterGroup>
                <Filter
                    className={filterClassName}
                    onClick={() => setPicking(true)}
                    ref={control}>
                    {label}
                    {!!getValue() && (
                        <Emoji>
                            {getUnicodeFlagIcon(getValue())}
                        </Emoji>
                    )}
                </Filter>
                {isResettable && (
                    <Filter
                        className="destructive reset"
                        onClick={() => reset()}>
                        <Reset size={16} />
                    </Filter>
                )}
            </ResettableFilterGroup>
            <EntityPicker
                {...entity}
                {...picker}
                picked={getValue() ? { id: getValue() } : null}
                mapper={optionMapper}
                show={!!picking}
                dismiss={() => setPicking(false)}
                cancelAction={() => ({
                    label: formatMessage({
                        id: 'action_cancel',
                        defaultMessage: 'Cancel'
                    }),
                    effect: 'neutral',
                    onClick: () => setPicking(false)
                })}
                doneAction={({ picked }) => ({
                    label: formatMessage({
                        id: 'action_done',
                        defaultMessage: 'Done'
                    }),
                    effect: 'constructive',
                    onClick: () => update({ [name]: picked?.id ?? null })
                })}
                salt={salt} />
        </>
    )
}

export default props => {
    const {
        territories,
        holidayTerritories
    } = useI18n()

    const entities = (props.picker?.mode === 'holidayTerritories') ?
        holidayTerritories.map(({ code, name, regions = [] }) => ({
            id: code,
            name,
            entities: regions.map(({ code, name }) => ({
                id: code,
                name,
                type: 'region'
            }))
        }))
        : Object.entries(omit(territories, 'ZZ')).map(([id, name]) => ({
            id,
            name
        }))

    return (
        <PickCountryField
            {...props}
            picker={{
                type: 'country',
                fixed: entities,
                creatable: false
            }} />
    )
}